import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Stack,
  IconButton,
  Checkbox,
  Radio,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableChoiceItem({ id, choice, index, onUpdate, onRemove, allowMultiple }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleChange = (e) => {
    const value = e.target.value;
    // Allow empty value while typing, just update the state
    onUpdate(index, value);
  };

  const handleBlur = (e) => {
    const value = e.target.value.trim();
    if (value === '') {
      // Only remove when field is blurred and empty
      onRemove(index);
    } else {
      // Update with trimmed value on blur
      onUpdate(index, value);
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1,
      }}
    >
      {/* Drag Handle */}
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          touchAction: 'none',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIcon fontSize="small" />
      </Box>

      {/* Show Checkbox or Radio based on allow_multiple */}
      {allowMultiple ? (
        <Checkbox disabled />
      ) : (
        <Radio disabled />
      )}

      {/* Editable TextField */}
      <TextField
        fullWidth
        size="small"
        value={choice}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={`Option ${index + 1}`}
        variant='standard'
      />

      {/* Remove Button */}
      <IconButton
        size="small"
        onClick={() => onRemove(index)}
        sx={{ color: 'text.secondary' }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export default function MultipleChoiceOptions({ data, updateOptions }) {
  const { t } = useTranslation(['procedures']);
  const [choices, setChoices] = useState(data.options?.choices || []);
  const [newChoiceValue, setNewChoiceValue] = useState('');
  const newChoiceInputRef = useRef(null);

  // Update local state when data changes from parent
  useEffect(() => {
    setChoices(data.options?.choices || []);
  }, [data.options?.choices]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = choices.findIndex((_, i) => i === active.id);
      const newIndex = choices.findIndex((_, i) => i === over.id);

      const reordered = arrayMove(choices, oldIndex, newIndex);
      setChoices(reordered);
      updateOptions({ choices: reordered });
    }
  };

  const handleUpdateChoice = (index, value) => {
    const updated = [...choices];
    updated[index] = value;
    setChoices(updated);
    updateOptions({ choices: updated });
  };

  const handleRemoveChoice = (index) => {
    const updated = choices.filter((_, i) => i !== index);
    setChoices(updated);
    updateOptions({ choices: updated });
  };

  const handleAddNewChoice = () => {
    if (newChoiceValue.trim()) {
      const updated = [...choices, newChoiceValue.trim()];
      setChoices(updated);
      updateOptions({ choices: updated });
      setNewChoiceValue('');

      // Focus back on the input
      setTimeout(() => {
        newChoiceInputRef.current?.focus();
      }, 0);
    }
  };

  const handleNewChoiceKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNewChoice();
    }
  };

  const handleNewChoiceBlur = () => {
    handleAddNewChoice();
  };

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
        {t('procedures:step_options.multiple_choice_options')}
      </Typography>
      <Stack spacing={2}>
        {/* Sortable Choices List */}
        {choices.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              {t('procedures:step_options.options_count', { count: choices.length })}
            </Typography>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={choices.map((_, i) => i)}
                strategy={verticalListSortingStrategy}
              >
                {choices.map((choice, index) => (
                  <SortableChoiceItem
                    key={index}
                    id={index}
                    choice={choice}
                    index={index}
                    onUpdate={handleUpdateChoice}
                    onRemove={handleRemoveChoice}
                    allowMultiple={data.options?.allow_multiple || false}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Box>
        )}

        {/* New Choice Input (Always at bottom) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DragIcon fontSize="small" sx={{ color: 'transparent' }} />
          {data.options?.allow_multiple ? (
            <Checkbox disabled />
          ) : (
            <Radio disabled />
          )}
          <TextField
            fullWidth
            size="small"
            variant="standard"
            placeholder="Add option"
            value={newChoiceValue}
            onChange={(e) => setNewChoiceValue(e.target.value)}
            onKeyDown={handleNewChoiceKeyDown}
            onBlur={handleNewChoiceBlur}
            inputRef={newChoiceInputRef}
          />
          <IconButton size="small" sx={{ color: 'transparent' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Allow Multiple Selections */}
        <FormControlLabel
          control={
            <Switch
              checked={data.options?.allow_multiple || false}
              onChange={(e) => updateOptions({ allow_multiple: e.target.checked })}
            />
          }
          label="Allow Multiple Selections"
        />

        {/* Min/Max Selections (only shown when allow_multiple is true) */}
        {data.options?.allow_multiple && (
          <Box display="flex" gap={3} pt={2}>
            <TextField
              fullWidth
              type="number"
              label="Min Selections"
              value={data.options?.min_selections || 1}
              onChange={(e) => updateOptions({ min_selections: parseInt(e.target.value) || 1 })}
              inputProps={{ min: 1, max: choices.length }}
            />
            <TextField
              fullWidth
              type="number"
              label="Max Selections"
              value={data.options?.max_selections || 3}
              onChange={(e) => updateOptions({ max_selections: parseInt(e.target.value) || 3 })}
              inputProps={{ min: 1, max: choices.length }}
            />
          </Box>
        )}
      </Stack>
    </>
  );
}
